import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Copy, Check, ExternalLink } from "lucide-react";
import { useToast } from "@/hooks/use-toast";

const ENDPOINT_URL = "https://wjyaifrcyjmjxixsegij.supabase.co/functions/v1/receive-external-lead";

const TYPESCRIPT_EXAMPLE = `// Add this to your contact form submission handler
const submitToPageleads = async (formData: {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  company?: string;
  position?: string;
  message?: string;
}) => {
  try {
    const response = await fetch(
      '${ENDPOINT_URL}',
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          firstName: formData.firstName,
          lastName: formData.lastName,
          email: formData.email,
          phone: formData.phone,
          company: formData.company,
          position: formData.position,
          message: formData.message,
          sourceProject: 'pageleads', // Change this to identify your project
          apiKey: 'YOUR_PAGELEADS_API_KEY' // Get this from admin
        })
      }
    );
    
    const result = await response.json();
    
    if (!response.ok) {
      throw new Error(result.error || 'Failed to submit lead');
    }
    
    return { success: true, leadId: result.leadId };
  } catch (error) {
    console.error('Pageleads submission error:', error);
    return { success: false, error: error.message };
  }
};`;

const PAYLOAD_SCHEMA = `{
  // Required fields (name - use ONE of these options)
  "fullName": "string",      // Full name (recommended for Meta Ads)
  // OR
  "firstName": "string",     // First name
  "lastName": "string",      // Last name

  // Required fields
  "email": "string",         // Valid email address
  "phone": "string",         // Phone number
  "sourceProject": "string", // Your project identifier (e.g., "pageleads")
  "apiKey": "string",        // API key for authentication

  // Optional fields
  "company": "string",       // Company name
  "position": "string",      // Job position/title
  "message": "string",       // Contact form message
  "customFields": {}         // Any additional data (stored in enriched_data)
}`;

const RESPONSE_SCHEMA = `// Success response (200)
{
  "success": true,
  "leadId": "uuid-of-created-lead"
}

// Error responses (400, 401, 500)
{
  "error": "Error message",
  "details": "Additional details (optional)"
}`;

const META_ADS_ZAPIER_PAYLOAD = `{
  "fullName": "{{lead.full_name}}",
  "email": "{{lead.email}}",
  "phone": "{{lead.phone_number}}",
  "company": "{{lead.company_name}}",
  "position": "{{lead.job_title}}",
  "message": "Lead from Meta Ads - Campaign: {{lead.ad_name}}",
  "sourceProject": "meta-ads",
  "apiKey": "YOUR_PAGELEADS_API_KEY",
  "customFields": {
    "campaign_name": "{{lead.campaign_name}}",
    "ad_set_name": "{{lead.adset_name}}",
    "ad_name": "{{lead.ad_name}}",
    "form_name": "{{lead.form_name}}",
    "platform": "{{lead.platform}}"
  }
}`;

export function PageleadsIntegrationDocs() {
  const { toast } = useToast();
  const [copiedSection, setCopiedSection] = useState<string | null>(null);

  const copyToClipboard = async (text: string, section: string) => {
    await navigator.clipboard.writeText(text);
    setCopiedSection(section);
    toast({
      title: "Skopiowano!",
      description: "Kod został skopiowany do schowka",
    });
    setTimeout(() => setCopiedSection(null), 2000);
  };

  const CopyButton = ({ text, section }: { text: string; section: string }) => (
    <Button
      variant="ghost"
      size="sm"
      className="absolute top-2 right-2 h-8 px-2"
      onClick={() => copyToClipboard(text, section)}
    >
      {copiedSection === section ? (
        <Check className="h-4 w-4 text-green-500" />
      ) : (
        <Copy className="h-4 w-4" />
      )}
    </Button>
  );

  return (
    <div className="space-y-6">
      {/* Overview Card */}
      <Card>
        <CardHeader>
          <CardTitle className="text-xl flex items-center gap-2">
            📦 Pageleads Integration
          </CardTitle>
          <CardDescription>
            Connect external projects to send leads to this admin panel
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid gap-4 md:grid-cols-2">
            <div className="space-y-2">
              <h4 className="font-semibold text-sm">Endpoint URL</h4>
              <div className="relative">
                <code className="block text-xs bg-muted p-3 rounded-md break-all pr-10">
                  {ENDPOINT_URL}
                </code>
                <CopyButton text={ENDPOINT_URL} section="endpoint" />
              </div>
            </div>
            <div className="space-y-2">
              <h4 className="font-semibold text-sm">Method</h4>
              <Badge variant="default" className="text-sm">POST</Badge>
            </div>
          </div>

          <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-4">
            <h4 className="font-semibold text-sm text-yellow-800 dark:text-yellow-200 mb-2">
              ⚠️ API Key Required
            </h4>
            <p className="text-sm text-yellow-700 dark:text-yellow-300">
              Contact the admin to get the <code className="bg-yellow-100 dark:bg-yellow-800 px-1 rounded">PAGELEADS_API_KEY</code>. 
              This key must be included in every request for authentication.
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Payload Schema */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Request Payload Schema</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="relative">
            <pre className="text-xs bg-muted p-4 rounded-md overflow-x-auto">
              {PAYLOAD_SCHEMA}
            </pre>
            <CopyButton text={PAYLOAD_SCHEMA} section="payload" />
          </div>
        </CardContent>
      </Card>

      {/* Response Schema */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Response Format</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="relative">
            <pre className="text-xs bg-muted p-4 rounded-md overflow-x-auto">
              {RESPONSE_SCHEMA}
            </pre>
            <CopyButton text={RESPONSE_SCHEMA} section="response" />
          </div>
        </CardContent>
      </Card>

      {/* TypeScript Example */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">TypeScript/React Example</CardTitle>
          <CardDescription>
            Copy this function and integrate it with your contact form
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="relative">
            <pre className="text-xs bg-muted p-4 rounded-md overflow-x-auto max-h-[400px]">
              {TYPESCRIPT_EXAMPLE}
            </pre>
            <CopyButton text={TYPESCRIPT_EXAMPLE} section="typescript" />
          </div>
        </CardContent>
      </Card>

      {/* Quick Integration Checklist */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">✅ Integration Checklist</CardTitle>
        </CardHeader>
        <CardContent>
          <ol className="space-y-3 text-sm">
            <li className="flex items-start gap-2">
              <span className="bg-primary text-primary-foreground rounded-full w-5 h-5 flex items-center justify-center text-xs flex-shrink-0">1</span>
              <span>Get the <code className="bg-muted px-1 rounded">PAGELEADS_API_KEY</code> from the admin</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="bg-primary text-primary-foreground rounded-full w-5 h-5 flex items-center justify-center text-xs flex-shrink-0">2</span>
              <span>Add the API key as an environment variable or secret in your project</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="bg-primary text-primary-foreground rounded-full w-5 h-5 flex items-center justify-center text-xs flex-shrink-0">3</span>
              <span>Copy the TypeScript example above into your project</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="bg-primary text-primary-foreground rounded-full w-5 h-5 flex items-center justify-center text-xs flex-shrink-0">4</span>
              <span>Update <code className="bg-muted px-1 rounded">sourceProject</code> to identify your project (e.g., "my-landing-page")</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="bg-primary text-primary-foreground rounded-full w-5 h-5 flex items-center justify-center text-xs flex-shrink-0">5</span>
              <span>Call <code className="bg-muted px-1 rounded">submitToPageleads()</code> in your form's onSubmit handler</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="bg-primary text-primary-foreground rounded-full w-5 h-5 flex items-center justify-center text-xs flex-shrink-0">6</span>
              <span>Test by submitting a lead - it should appear in this Admin panel with a "Pageleads" badge</span>
            </li>
          </ol>
        </CardContent>
      </Card>

      {/* Meta Ads via Zapier */}
      <Card className="border-orange-200 dark:border-orange-800">
        <CardHeader>
          <CardTitle className="text-xl flex items-center gap-2">
            📣 Meta Ads Integration (via Zapier)
          </CardTitle>
          <CardDescription>
            Connect Meta Lead Ads to this admin panel using Zapier
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="bg-orange-50 dark:bg-orange-900/20 border border-orange-200 dark:border-orange-800 rounded-lg p-4">
            <h4 className="font-semibold text-sm text-orange-800 dark:text-orange-200 mb-2">
              Why Zapier?
            </h4>
            <p className="text-sm text-orange-700 dark:text-orange-300">
              Zapier handles Meta's webhook authentication automatically and provides a no-code way to connect your Lead Ads forms.
            </p>
          </div>

          <div className="space-y-3">
            <h4 className="font-semibold text-sm">Step-by-step Zapier Setup:</h4>
            <ol className="space-y-3 text-sm">
              <li className="flex items-start gap-2">
                <span className="bg-orange-500 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs flex-shrink-0">1</span>
                <span>Create a new Zap in Zapier</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="bg-orange-500 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs flex-shrink-0">2</span>
                <span><strong>Trigger:</strong> Facebook Lead Ads → New Lead</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="bg-orange-500 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs flex-shrink-0">3</span>
                <span>Connect your Facebook account and select your Page & Form</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="bg-orange-500 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs flex-shrink-0">4</span>
                <span><strong>Action:</strong> Webhooks by Zapier → POST</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="bg-orange-500 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs flex-shrink-0">5</span>
                <span>Set URL to: <code className="bg-muted px-1 rounded text-xs">{ENDPOINT_URL}</code></span>
              </li>
              <li className="flex items-start gap-2">
                <span className="bg-orange-500 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs flex-shrink-0">6</span>
                <span>Set Payload Type to <strong>JSON</strong></span>
              </li>
              <li className="flex items-start gap-2">
                <span className="bg-orange-500 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs flex-shrink-0">7</span>
                <span>Copy the JSON payload below and map your Meta fields</span>
              </li>
            </ol>
          </div>

          <div className="space-y-2">
            <h4 className="font-semibold text-sm">Zapier JSON Payload Template:</h4>
            <div className="relative">
              <pre className="text-xs bg-muted p-4 rounded-md overflow-x-auto max-h-[300px]">
                {META_ADS_ZAPIER_PAYLOAD}
              </pre>
              <CopyButton text={META_ADS_ZAPIER_PAYLOAD} section="meta-payload" />
            </div>
          </div>

          <div className="space-y-2">
            <h4 className="font-semibold text-sm">Common Meta Lead Ads Fields:</h4>
            <div className="grid gap-2 text-xs">
              <div className="grid grid-cols-2 gap-2 bg-muted p-2 rounded">
                <code>{"{{lead.full_name}}"}</code>
                <span className="text-muted-foreground">Full Name (recommended)</span>
              </div>
              <div className="grid grid-cols-2 gap-2 bg-muted p-2 rounded">
                <code>{"{{lead.first_name}}"}</code>
                <span className="text-muted-foreground">First Name</span>
              </div>
              <div className="grid grid-cols-2 gap-2 bg-muted p-2 rounded">
                <code>{"{{lead.last_name}}"}</code>
                <span className="text-muted-foreground">Last Name</span>
              </div>
              <div className="grid grid-cols-2 gap-2 bg-muted p-2 rounded">
                <code>{"{{lead.email}}"}</code>
                <span className="text-muted-foreground">Email Address</span>
              </div>
              <div className="grid grid-cols-2 gap-2 bg-muted p-2 rounded">
                <code>{"{{lead.phone_number}}"}</code>
                <span className="text-muted-foreground">Phone Number</span>
              </div>
              <div className="grid grid-cols-2 gap-2 bg-muted p-2 rounded">
                <code>{"{{lead.company_name}}"}</code>
                <span className="text-muted-foreground">Company Name</span>
              </div>
              <div className="grid grid-cols-2 gap-2 bg-muted p-2 rounded">
                <code>{"{{lead.job_title}}"}</code>
                <span className="text-muted-foreground">Job Title</span>
              </div>
              <div className="grid grid-cols-2 gap-2 bg-muted p-2 rounded">
                <code>{"{{lead.campaign_name}}"}</code>
                <span className="text-muted-foreground">Campaign Name</span>
              </div>
              <div className="grid grid-cols-2 gap-2 bg-muted p-2 rounded">
                <code>{"{{lead.ad_name}}"}</code>
                <span className="text-muted-foreground">Ad Name</span>
              </div>
            </div>
          </div>

          <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
            <h4 className="font-semibold text-sm text-blue-800 dark:text-blue-200 mb-2">
              💡 Tips
            </h4>
            <ul className="text-sm text-blue-700 dark:text-blue-300 space-y-1">
              <li>• Meta field names may vary based on your form configuration</li>
              <li>• Test with a sample lead before going live</li>
              <li>• Leads will appear with an orange "📣 Meta Ads" badge</li>
              <li>• Campaign data is stored in enriched_data for reference</li>
            </ul>
          </div>
        </CardContent>
      </Card>

      {/* Features */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">What happens when a lead is submitted?</CardTitle>
        </CardHeader>
        <CardContent>
          <ul className="space-y-2 text-sm">
            <li className="flex items-center gap-2">
              <Badge variant="outline" className="text-xs">✓</Badge>
              Lead is validated (email format, required fields)
            </li>
            <li className="flex items-center gap-2">
              <Badge variant="outline" className="text-xs">✓</Badge>
              Lead is saved to database with source project identifier
            </li>
            <li className="flex items-center gap-2">
              <Badge variant="outline" className="text-xs">✓</Badge>
              Telegram notification is sent to admins
            </li>
            <li className="flex items-center gap-2">
              <Badge variant="outline" className="text-xs">✓</Badge>
              Lead appears in Admin panel with source badge (📦 Pageleads or 📣 Meta Ads)
            </li>
            <li className="flex items-center gap-2">
              <Badge variant="outline" className="text-xs">✓</Badge>
              Lead can be enriched and pushed to CRM from Admin panel
            </li>
          </ul>
        </CardContent>
      </Card>
    </div>
  );
}
